// Aerialways — cable cars (Pendelbahn: two counterweighted cabins, one up
// while one comes down) and gondolas (Umlaufbahn: many cabins circulating on a
// loop), on their real OSM alignments. Like the funicular, everything the
// cabins DO is server-driven (snapshot lift.a) through the shared resolver in
// game/lifts.js — this file only draws and carries (cabinPosHinted is that
// resolver with a remembered segment, gate check-lifts.mjs holds them equal).
//
// Two cables per line, like in real life: the up-track and the down-track of
// the system, each a sagged catenary through the baked support saddles
// (lifts.json.aerial, clearance solved against the full-res grid at bake
// time). Pendel lines additionally show a haul rope under each track cable.
//
// Draw-call budget: per line ONE merged cable mesh, ONE merged structure mesh
// (pylons + station portals), ONE InstancedMesh for all cabins (the per-cabin
// transform lives in `cars[k]` dummies so the station zones can keep reading
// `cars[i].position` exactly like a funicular car). Chair lines share theirs
// across lines (_buildChairs): cables + structures one mesh per LIFT_CELL_M
// cell, the chairs two InstancedMeshes over ONE distance-sorted buffer.
//
// R8d6: the chair network is built in STEPS (_buildChairsSteps, run by
// decode.js runSteps under 'lifts.chairs'; it was ONE ~130 ms task after the
// Beam: every chair line's boxes, then every cell's merge): a step per chair
// line (its cable segments binned, its supports' parts, its chairs), per
// cable cell, per structure cell, then the chair pair. The same code in the
// same order as the one-shot (_buildChairs = its drain) — the same scene
// order and bytes (gate check-lifts.mjs `steps`).
import * as THREE from 'three';
import { aerialLines, dist3 } from '../../game/lifts.js';
import { makeLabel } from './funicular.js';
import { fetchJsonOnce } from './fetchonce.js';
import { slice, act } from './sched.js';
import { runSteps } from './decode.js';
import { classDraw } from './device.js';
import { vrsCap } from './vrstyle/index.js';
import { cpuShort } from './adaptres.js';
import { thinRibbonMaterial, ribbonOffsets, RIBBON_ORDER } from './wire.js';
import { DARK, STEEL, CONCRETE, mergeParts, box, pendelCabinParts, gondolaCabinParts }
  from './cabinparts.js';

// cross-ribbon cable: two perpendicular thin quads per segment, so the rope
// reads as a line from every viewing angle without cylinder tri counts. The
// quads are ONE-sided geometry (the horizontal one faces up, the vertical one
// faces one side of the line, and every cable of a line runs the same way),
// so the material MUST be double-sided: front-side only, a rope seen from
// below and from the line's other side had no face left to draw (owner, cable
// gone while looking up at the chairs) — gate check-lifts.mjs `faces`.
function cableGeo(polylines, w) {
  const pos = [], idx = [];
  for (const pts of polylines) {
    for (let i = 0; i < pts.length - 1; i++) {
      const a = pts[i], b = pts[i + 1];
      const dx = b[0] - a[0], dz = b[2] - a[2];
      const l = Math.hypot(dx, dz) || 1;
      const px = (-dz / l) * w / 2, pz = (dx / l) * w / 2;
      const base = pos.length / 3;
      // vertical quad
      pos.push(a[0], a[1] - w / 2, a[2], a[0], a[1] + w / 2, a[2],
        b[0], b[1] - w / 2, b[2], b[0], b[1] + w / 2, b[2]);
      // horizontal quad
      pos.push(a[0] - px, a[1], a[2] - pz, a[0] + px, a[1], a[2] + pz,
        b[0] - px, b[1], b[2] - pz, b[0] + px, b[1], b[2] + pz);
      for (const o of [0, 4]) {
        idx.push(base + o, base + o + 1, base + o + 2,
          base + o + 1, base + o + 3, base + o + 2);
      }
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  // every vertex pair straddles its rope axis: the thin-ribbon widening (wire.js)
  g.setAttribute('aOff', new THREE.BufferAttribute(ribbonOffsets(pos), 3));
  g.setIndex(idx);
  g.computeBoundingSphere();
  return g;
}

// Open 4-seater chair. Origin at the FOOTREST CENTER (drop = 2.2 under the
// rope), forward = +Z. Seat pan + back are WHITE so the shared InstancedMesh's
// per-instance color paints them per line; frame/hanger/bar stay dark (dark ×
// instanceColor still reads as dark metal). 11 boxes ≈ 132 tris. `lean`: the
// seat pan + back (24 tris) that still draw past the detail range
// (CHAIR_DETAIL_PX); the other 9 are the 0.06-0.1 m frame, bars, hanger, grip.
function chairParts(drop) {
  const SEAT = [1, 1, 1];
  const P = [];
  P.push({ geo: box(2.4, 0.12, 0.6, 0, 0.55, -0.05), color: SEAT, lean: true });          // seat pan
  P.push({ geo: box(2.4, 0.78, 0.1, 0, 0.98, -0.38, 0, 0, -0.16), color: SEAT, lean: true }); // back, tilted
  for (const s of [1, -1]) {
    P.push({ geo: box(0.08, 0.5, 0.66, s * 1.18, 0.72, -0.05), color: DARK }); // side frames
  }
  P.push({ geo: box(2.3, 0.07, 0.09, 0, 0.06, 0.28), color: DARK });          // footrest bar
  P.push({ geo: box(2.3, 0.07, 0.07, 0, 0.95, 0.5), color: DARK });           // safety bar (down)
  for (const s of [1, -1]) {
    P.push({ geo: box(0.06, 0.52, 0.06, s * 1.12, 1.16, 0.42, 0, 0, 0.35), color: DARK });
  }
  P.push({ geo: box(0.1, 0.95, 0.14, 0, 1.82, -0.42, 0, 0, 0), color: DARK }); // hanger post
  P.push({ geo: box(0.09, 0.55, 0.12, 0, 2.05, -0.2, 0, 0, 0.7), color: DARK }); // hanger bend
  P.push({ geo: box(0.2, 0.16, 0.5, 0, drop - 0.08, 0), color: DARK });        // grip
  return P;
}

// fixed seat palette, keyed by line-name hash — chairs deliberately skip the
// AI livery pass (small, distant, and a livery cabins hint would move chair
// positions; the guard lives in game/lifts.js aerialLines)
const CHAIR_PALETTE = [
  [0.82, 0.18, 0.16], [0.94, 0.52, 0.10], [0.95, 0.77, 0.06], [0.13, 0.62, 0.74],
  [0.16, 0.35, 0.78], [0.22, 0.62, 0.25], [0.55, 0.27, 0.72], [0.85, 0.29, 0.48],
];
function chairColor(name) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h + name.charCodeAt(i)) % 997;
  return CHAIR_PALETTE[h % CHAIR_PALETTE.length];
}

// A far chair line's chairs are re-posed every CHAIR_FAR_EVERY-th frame only
// (staggered by line): past CHAIR_FAR_M from the line's xz box a chair (2.4 m)
// is ~2 px at 1x, and the 2.5 m/s loop hops 0.08 m per re-pose (≤ 3 px at
// the optic's max zoom). Draw only — every car.position stays exact every
// frame (the station zones, the ride re-pin and nearest() read it) — except
// where the CPU is short (quest-cpu 2026-11-02 the quest; all-res 2026-11-02
// adaptres cpuShort: the quest, or a draw rung given way; farLazy): there a far chair line's
// off-stagger frame skips its cars entirely, so its car.position is its last
// pose (≤ CHAIR_FAR_EVERY − 1 frames old, = its matrix). Every reader of a
// chair position is local (station zone ≤ 16 m of a terminal, nearest()
// < 18 m, the ride, no walk-in carry on chairs: carryHx 0), and a line past
// CHAIR_FAR_M from its own box is past all of them.
export const CHAIR_FAR_M = 1500;
export const CHAIR_FAR_EVERY = 3;

// The chair draw cut (TRI-CUTS; 646 chairs × 132 tris were drawn wherever
// they were). Each frame the chairs are sorted by distance into ONE instance
// buffer; per camera (onBeforeRender: the full view, the optic, each XR eye)
// the LEAN mesh draws the prefix whose chair spans >= CHAIR_MIN_PX and the
// DETAIL mesh (the other 9 boxes) the prefix that spans >= CHAIR_DETAIL_PX
// (the 0.07 m bars ≈ 0.18 px, the 0.1 m hanger ≈ 0.25 px there). The span is CHAIR_SPAN_M at the camera's
// own pixel scale (projection × viewport, so a zoom or the optic draws
// farther), at the frustum corner (the widest stretch) — chairRange.
// Positions and poses are untouched: only the instance count is cut.
export const CHAIR_SPAN_M = 2.4;
export const CHAIR_MIN_PX = 1;
export const CHAIR_DETAIL_PX = 6;
// chair cables + structures: one mesh per cell of LIFT_CELL_M, so they cull
export const LIFT_CELL_M = 2048;
// the class's cell (quest-merge 2026-11-01): LIFT_CELL_M × CLASS_DRAW lift_cell
// (device.js; quest 8 = 16384 m: ONE cable + ONE structure mesh for every
// chair line, no cull — a draw is the Quest frame's unit)
export const liftCellM = (cls) => LIFT_CELL_M * classDraw('lift_cell', cls);
const cellKeyOf = (C) => (x, z) => Math.floor(x / C) + ',' + Math.floor(z / C);

// the distance within which a chair spans >= px pixels for `camera` drawing
// into a viewport `vpH` px tall (perspective; anything else: Infinity)
export function chairRange(camera, vpH, px) {
  const e = camera.projectionMatrix.elements;
  if (!(px > 0) || e[11] !== -1) return Infinity;     // no cut / not a perspective projection
  const corner = Math.sqrt(1 + 1 / (e[0] * e[0]) + 1 / (e[5] * e[5]));
  return CHAIR_SPAN_M * e[5] * (vpH / 2) * corner / px;
}

// aerialCabinPos (game/lifts.js, the server's resolver) with the segment
// search started at the cabin's previous segment (hint[k]) instead of the
// cable's first point: a cabin moves < 1 segment (≤ 12 m) per frame, so the
// walk is 0–2 steps instead of ~67 (677 cabins, 45k steps a frame on the
// St. Moritz bake). Same segment (the smallest i ≥ 1 with cum[i] ≥ d, else
// the last), same arithmetic — identical values (gate check-lifts.mjs).
// stats.steps counts the walk (the gate's cost row).
export const cabStats = { steps: 0, calls: 0 };
export function cabinPosHinted(line, s, k, out, hint) {
  let side, sv, dir;
  if (line.system === 'pendel') {
    side = k ? 1 : 0;
    sv = k ? 1 - s : s;
    dir = k ? -1 : 1;
  } else {
    const ph = ((s + k / line.cabins) % 1 + 1) % 1;
    if (ph <= 0.5) { side = 0; sv = ph * 2; dir = 1; } else { side = 1; sv = 2 - ph * 2; dir = -1; }
  }
  const path = line.sides[side];
  const d = Math.max(0, Math.min(1, sv)) * path.length;
  const cum = path.cum, pts = path.points, last = cum.length - 1;
  // (a NaN d scans to 1, as the linear scan does)
  let i = hint && d >= 0 ? Math.min(Math.max(1, hint[k]), Math.max(1, last)) : 1;
  let n = 0;
  while (i > 1 && cum[i - 1] >= d) { i--; n++; }
  while (i < last && cum[i] < d) { i++; n++; }
  cabStats.steps += n;
  cabStats.calls++;
  if (hint) hint[k] = i;
  const t = (d - cum[i - 1]) / Math.max(1e-6, cum[i] - cum[i - 1]);
  const a = pts[i - 1], b = pts[i];
  const len = dist3(a, b) || 1;
  const p = out.pos, g = out.tangent;
  p[0] = a[0] + (b[0] - a[0]) * t; p[1] = a[1] + (b[1] - a[1]) * t; p[2] = a[2] + (b[2] - a[2]) * t;
  g[0] = (b[0] - a[0]) / len; g[1] = (b[1] - a[1]) / len; g[2] = (b[2] - a[2]) / len;
  out.cableY = p[1];
  p[1] -= line.cabDrop;
  out.side = side; out.sv = sv; out.dir = dir;
  return out;
}

// ------------------------------------------------------------------ class
export class Aerialway {
  constructor(scene, baseUrl, hf) {
    this.scene = scene;
    this.hf = hf || null;
    this.lines = [];        // { path, kind, system, cabins, cabDrop, cars, s, targetS }
    this.statics = [];      // the static meshes (cables, pylons / stations): viscull.js's lift units
    this.ready = false;
    this._baseUrl = baseUrl;
    this._loadP = null;     // start(): the load, once
    this._tick = 0;         // frame count: the far chair lines' stagger
    // gates' red twins only: hinted false = the linear segment scan from the
    // cable's start, farEvery 1 = every chair re-posed every frame
    this.hinted = true;
    this.farEvery = CHAIR_FAR_EVERY;
    // a far chair line's off-stagger frame skips its cars (positions = last
    // pose): null = adaptres cpuShort decides (the quest, or a CPU the draw
    // rung measured short; CHAIR_FAR_M note); a gate sets true / false
    this.farLazy = null;
    this.yawCache = true;
    // the chair draw cut (gate red twins: 0 = no cut, every chair drawn)
    this.chairMinPx = CHAIR_MIN_PX;
    this.chairDetailPx = CHAIR_DETAIL_PX;
    this._chairs = null;
    // the cable material's side (gate red twin only: THREE.FrontSide)
    this.cableSide = THREE.DoubleSide;
    this._cableMat = null;
  }

  // one material for every cable mesh (per-line ropes + the chair network): a thin ribbon
  // (wire.js — never thinner than RIBBON_MIN_PX on screen, the coverage as its alpha)
  _cableMaterial() {
    if (!this._cableMat) {
      this._cableMat = thinRibbonMaterial(new THREE.MeshBasicMaterial({ color: 0x26282b, fog: true,
        side: this.cableSide }));
    }
    return this._cableMat;
  }

  // a cable mesh: the thin-ribbon material, drawn with the wires
  _cableMesh(geo) {
    const m = new THREE.Mesh(geo, this._cableMaterial());
    m.renderOrder = RIBBON_ORDER;
    return m;
  }

  // main.js starts the fetch in its load phase 1 (as Funicular.start)
  start() {
    if (!this._loadP) this._loadP = this._load(this._baseUrl);
    return this._loadP;
  }

  async _load(baseUrl) {
    try {
      // lifts.json + livery.json: one fetch each, shared with the Funicular
      const lifts = await fetchJsonOnce(`${baseUrl}/lifts.json`);
      if (!lifts) { this.ready = true; if (this.onReady) this.onReady([]); return; }
      const livery = await fetchJsonOnce(`${baseUrl}/livery.json`).catch(() => null);
      const chairs = [], lines = [];
      // one line per step, sliced (the build ran as one ~4 s task over the
      // Map); this.lines appears whole at the end, as before. (the fetches
      // land in the same task as the funicular's: its cars first, then a
      // slice before the resolve)
      await slice();
      const all = act('lifts.resolve', () => aerialLines(lifts, livery));
      for (const a of all) {
        const prof = livery && livery.lines && livery.lines[a.name];
        const line = this._lineRec(a);
        // chair lines are drawn in ONE cross-line group after the loop, but
        // their line records are pushed HERE, in resolver order — the lines[]
        // index must equal the server's aerial index (snapshot lift.a)
        if (a.kind === 'chair_lift') chairs.push(line);
        else { await slice(); act('lifts.build', () => this._build(line, prof)); }
        lines.push(line);
      }
      if (chairs.length) { await slice(); await runSteps(this._buildChairsSteps(chairs), 'lifts.chairs'); }
      this.lines = lines;
      this.ready = true;
      if (this.onReady) this.onReady(this.lines.map((l) => l.path));
    } catch { this.ready = true; if (this.onReady) this.onReady([]); }
  }

  // the line record of resolved line `a` (aerialLines)
  _lineRec(a) {
    return {
      // `path` mirrors a funicular line's shape so StationZones can treat
      // every ride uniformly (name split, terminals, samplePath)
      path: { name: a.name, points: a.sides[0].points,
        cum: a.sides[0].cum, length: a.sides[0].length },
      kind: a.kind, system: a.system, cabins: a.cabins,
      cabDrop: a.cabDrop, line: a,
      s: 0, targetS: 0, cars: [], prev: [], instBase: 0,
      seg: null, box: lineBox(a), posed: false,
    };
  }

  _build(line, prof) {
    const a = line.line;
    // ---- cables (both tracks; pendel adds a haul rope under each)
    const polys = [a.sides[0].points, a.sides[1].points];
    if (a.system === 'pendel') {
      for (const side of [0, 1]) {
        polys.push(a.sides[side].points.map((p) => [p[0], p[1] - 1.0, p[2]]));
      }
    }
    const cables = this._cableMesh(cableGeo(polys, a.system === 'pendel' ? 0.13 : 0.1));
    cables.name = 'aerial-cable:' + a.name;
    this.scene.add(cables);
    this.statics.push(cables);

    // ---- structures: pylons at intermediate supports, portals at stations
    const parts = [];
    const sup = a.sup;
    for (let i = 0; i < sup.length; i++) {
      const s = sup[i];
      const p0 = sup[Math.max(0, i - 1)], p1 = sup[Math.min(sup.length - 1, i + 1)];
      const dx = p1[0] - p0[0], dz = p1[2] - p0[2];
      const dl = Math.hypot(dx, dz) || 1;
      const ux = dx / dl, uz = dz / dl;             // along the line
      // orient a box's X axis along the PERP (px,pz): rotateY(t), X-axis = (cos t, 0, -sin t)
      const px = -uz, pz = ux;
      const tAcross = Math.atan2(-pz, px);
      const tAlong = Math.atan2(-uz, ux);
      const base = Number.isFinite(s[3]) ? s[3] : s[1] - 12;
      const top = s[1];
      if (i === 0 || i === sup.length - 1) {
        // station portal: columns beside both tracks, head beam, roof, platform
        const colH = top - base + 1.0;
        for (const sgn of [1, -1]) {
          parts.push({ geo: box(0.55, colH, 0.55,
            s[0] + px * sgn * (a.gauge / 2 + 1.2), base + colH / 2,
            s[2] + pz * sgn * (a.gauge / 2 + 1.2), tAcross), color: CONCRETE });
        }
        parts.push({ geo: box(a.gauge + 3.4, 0.85, 1.5, s[0], top + 0.55, s[2],
          tAcross), color: CONCRETE });
        parts.push({ geo: box(a.gauge + 4.2, 0.22, 7.0, s[0], top + 1.15, s[2],
          tAcross), color: CONCRETE });
        const floorY = top - a.cabDrop;
        parts.push({ geo: box(a.gauge + 3.4, 0.3, 5.2, s[0], floorY - 0.22, s[2],
          tAcross), color: [0.5, 0.49, 0.47] });
      } else {
        // pylon: tapered mast + crossarm + sheave batteries over both tracks
        const H = top - base + 1.1;
        const mast = new THREE.CylinderGeometry(0.42, 0.85, H, 6);
        mast.translate(s[0], base + H / 2, s[2]);
        parts.push({ geo: mast, color: STEEL });
        parts.push({ geo: box(a.gauge + 2.2, 0.5, 0.7, s[0], top + 0.25, s[2],
          tAcross), color: STEEL });
        for (const sgn of [1, -1]) {
          parts.push({ geo: box(0.32, 0.42, 1.9,
            s[0] + px * sgn * a.gauge / 2, top - 0.1,
            s[2] + pz * sgn * a.gauge / 2, tAlong), color: DARK });
        }
      }
    }
    const struct = new THREE.Mesh(mergeParts(parts),
      new THREE.MeshLambertMaterial({ vertexColors: true, fog: true }));
    struct.name = 'aerial-struct:' + a.name;
    this.scene.add(struct);
    this.statics.push(struct);

    // ---- cabins: one InstancedMesh, per-cabin dummies drive the matrices
    const cabParts = a.system === 'pendel'
      ? pendelCabinParts(prof, a.cabDrop) : gondolaCabinParts(prof, a.cabDrop);
    const n = a.system === 'pendel' ? 2 : a.cabins;
    const inst = new THREE.InstancedMesh(mergeParts(cabParts),
      new THREE.MeshLambertMaterial({ vertexColors: true, fog: true }), n);
    // cabins span the line: its cables' sphere + the hang and the cabin
    inst.boundingSphere = lineSphere(a, a.cabDrop + 6);
    inst.name = 'aerial-cabins:' + a.name;
    this.scene.add(inst);
    line.inst = inst;
    for (let k = 0; k < n; k++) {
      line.cars.push(new THREE.Object3D());
      line.prev.push(null);
    }
    line.seg = new Int32Array(n).fill(1);
    // name label over each station portal
    for (const end of [0, sup.length - 1]) {
      const label = makeLabel(a.name);
      label.position.set(sup[end][0], sup[end][1] + 2.6, sup[end][2]);
      this.scene.add(label);
    }
    // carry half-extents (cabin interior box, floor-origin)
    line.carryHx = a.system === 'pendel' ? 1.3 : 1.0;
    line.carryHz = a.system === 'pendel' ? 1.85 : 1.05;
    // walkable interior (tighter than the carry box: inside the walls) and
    // the ride-sign mount on an end wall, kept below the window band
    line.innerHx = a.system === 'pendel' ? 1.05 : 0.72;
    line.innerHz = a.system === 'pendel' ? 1.6 : 0.72;
    line.signMount = a.system === 'pendel'
      ? { y: 0.95, z: 1.68, s: 0.85 } : { y: 0.55, z: 0.86, s: 0.55 };
  }

  // All chair lines share their cable and structure meshes (one per
  // LIFT_CELL_M cell: 9 + 9 on St. Moritz, each culled) and the chair pair
  // (lean + detail over one buffer) — not the per-line 5 draws (65) that
  // would eat the whole extra_draw_reserve. No name labels either (26 sprites
  // saved — the station-zone sign names the lift). In steps (R8d6): a step
  // per chair line, per cable cell, per structure cell, then the chairs;
  // _buildChairs = the drain (the same code, the same order).
  _buildChairs(chairLines) { drain(this._buildChairsSteps(chairLines)); }
  *_buildChairsSteps(chairLines) {
    const cables = new Map(), structs = new Map();
    const cellKey = cellKeyOf(liftCellM());      // the class's cell (built after detectDevice)
    const cell = (m, k) => { let v = m.get(k); if (!v) m.set(k, v = []); return v; };
    let total = 0;
    const cars = [], col = [];
    for (const line of chairLines) {
      const a = line.line;
      // a cable segment's own quads (cableGeo: no vertex shared between
      // segments), binned by its midpoint
      for (const side of a.sides) {
        const pts = side.points;
        for (let i = 0; i + 1 < pts.length; i++) {
          cell(cables, cellKey((pts[i][0] + pts[i + 1][0]) / 2, (pts[i][2] + pts[i + 1][2]) / 2))
            .push([pts[i], pts[i + 1]]);
        }
      }
      this._chairStructParts(a, (s) => cell(structs, cellKey(s[0], s[2])));
      line.instBase = total;
      line.chairIx = chairLines.indexOf(line);
      total += a.cabins;
      const c = chairColor(line.path.name);
      for (let k = 0; k < a.cabins; k++) {
        const car = new THREE.Object3D();
        line.cars.push(car);
        line.prev.push(null);
        cars.push(car);
        col.push(c[0], c[1], c[2]);
      }
      line.seg = new Int32Array(a.cabins).fill(1);
      // you cannot walk into a moving chair: no walk-in carry — the station
      // sign beam (stationzones) is the only board path
      line.carryHx = 0;
      line.carryHz = 0;
      yield;
    }
    for (const [k, polys] of cables) {
      const m = this._cableMesh(cableGeo(polys, 0.07));
      m.name = 'chair-cables@' + k;
      this.scene.add(m);
      this.statics.push(m);
      yield;
    }
    const structMat = new THREE.MeshLambertMaterial({ vertexColors: true, fog: true });
    for (const [k, parts] of structs) {
      const m = new THREE.Mesh(mergeParts(parts), structMat);
      m.name = 'chair-structs@' + k;
      this.scene.add(m);
      this.statics.push(m);
      yield;
    }
    // the chairs: lean + detail over ONE buffer (matrix + colour), slot i =
    // the i-th nearest chair (_packChairs); the count is cut per camera
    const P = chairParts(chairLines[0].cabDrop);
    const mat = new THREE.MeshLambertMaterial({ vertexColors: true, fog: true });
    const inst = new THREE.InstancedMesh(mergeParts(P.filter((q) => q.lean)), mat, total);
    const instD = new THREE.InstancedMesh(mergeParts(P.filter((q) => !q.lean)), mat, total);
    for (let i = 0; i < total; i++) {
      _color.setRGB(col[i * 3], col[i * 3 + 1], col[i * 3 + 2]);
      inst.setColorAt(i, _color);
    }
    instD.instanceMatrix = inst.instanceMatrix;
    instD.instanceColor = inst.instanceColor;
    inst.frustumCulled = instD.frustumCulled = false;    // the network spans the whole map
    inst.name = 'chair-seats';
    instD.name = 'chair-seats-detail';
    const C = this._chairs = { inst, instD, cars, col: new Float32Array(col), n: total,
      order: Int32Array.from({ length: total }, (_, i) => i),
      d: new Float32Array(total), dist: new Float32Array(total).fill(Infinity),
      origin: new THREE.Vector3(), packed: false,
      // chair g's line (its poseTick: _packChairs copies only fresh poses)
      lines: chairLines, lineOf: new Uint16Array(total) };
    for (const line of chairLines) C.lineOf.fill(line.chairIx, line.instBase, line.instBase + line.cars.length);
    const cut = (mesh, px) => (renderer, scene, camera) => {
      mesh.count = this._chairCount(renderer, camera, px());
    };
    inst.onBeforeRender = cut(inst, () => this.chairMinPx);
    instD.onBeforeRender = cut(instD, () => this.chairDetailPx);
    inst.count = instD.count = 0;
    C.inst.instanceColor.needsUpdate = true;
    this.scene.add(inst, instD);
  }

  // how many of the sorted chairs `camera` draws at px (the prefix within
  // chairRange of it; its distance to the sort origin widens the range, so a
  // chair is never cut nearer than the range from the camera itself)
  _chairCount(renderer, camera, px) {
    const C = this._chairs;
    if (!C.packed) return 0;
    if (!(px > 0)) return C.n;
    renderer.getCurrentViewport(_vp);
    const R = vrsCap(chairRange(camera, _vp.w, px));   // THE VR LOOK: no chair past the void (vrstyle/)
    if (R === Infinity) return C.n;
    const lim = R + _cp.setFromMatrixPosition(camera.matrixWorld).distanceTo(C.origin);
    let lo = 0, hi = C.n;
    while (lo < hi) { const m = (lo + hi) >> 1; if (C.dist[m] <= lim) lo = m + 1; else hi = m; }
    return lo;
  }

  // slot i <- the i-th nearest chair (to p): its last pose and its colour.
  // An insertion sort from last frame's order (nearly sorted: ~linear). An
  // unmoved order rewrites only the chairs posed this frame (a far line off
  // its stagger keeps its slots' last pose: the same bytes).
  _packChairs(p) {
    const C = this._chairs, { order, d, dist, cars, lines, lineOf } = C, n = C.n, tick = this._tick;
    C.origin.copy(p);
    for (let g = 0; g < n; g++) d[g] = cars[g].position.distanceTo(p);
    let moved = !C.packed;
    for (let i = 1; i < n; i++) {
      const g = order[i], v = d[g];
      let j = i - 1;
      while (j >= 0 && d[order[j]] > v) { order[j + 1] = order[j]; j--; }
      if (j !== i - 1) { order[j + 1] = g; moved = true; }
    }
    const m = C.inst.instanceMatrix.array, ca = C.inst.instanceColor.array, col = C.col;
    for (let i = 0; i < n; i++) {
      const g = order[i];
      dist[i] = d[g];
      if (moved || lines[lineOf[g]].poseTick === tick) cars[g].matrix.toArray(m, i * 16);
      if (moved) { ca[i * 3] = col[g * 3]; ca[i * 3 + 1] = col[g * 3 + 1]; ca[i * 3 + 2] = col[g * 3 + 2]; }
    }
    C.inst.instanceMatrix.needsUpdate = true;
    if (moved) C.inst.instanceColor.needsUpdate = true;
    C.packed = true;
  }

  // chair pylons/terminals are leaner than gondola portals: low tapered mast +
  // crossarm + sheaves; stations are 2 columns, a horizontal bullwheel disc,
  // a flat canopy and the load ramp at footrest height. at(s): the parts
  // list support s goes to (its cell's)
  _chairStructParts(a, at) {
    const sup = a.sup;
    for (let i = 0; i < sup.length; i++) {
      const s = sup[i];
      const parts = at(s);
      const p0 = sup[Math.max(0, i - 1)], p1 = sup[Math.min(sup.length - 1, i + 1)];
      const dx = p1[0] - p0[0], dz = p1[2] - p0[2];
      const dl = Math.hypot(dx, dz) || 1;
      const ux = dx / dl, uz = dz / dl;
      const px = -uz, pz = ux;
      const tAcross = Math.atan2(-pz, px);
      const tAlong = Math.atan2(-uz, ux);
      const base = Number.isFinite(s[3]) ? s[3] : s[1] - 9;
      const top = s[1];
      if (i === 0 || i === sup.length - 1) {
        const colH = top - base + 0.6;
        for (const sgn of [1, -1]) {
          parts.push({ geo: box(0.5, colH, 0.5,
            s[0] + px * sgn * (a.gauge / 2 + 0.9), base + colH / 2,
            s[2] + pz * sgn * (a.gauge / 2 + 0.9), tAcross), color: CONCRETE });
        }
        const wheel = new THREE.CylinderGeometry(a.gauge / 2 + 0.3, a.gauge / 2 + 0.3, 0.22, 12);
        wheel.translate(s[0], top, s[2]);
        parts.push({ geo: wheel, color: DARK });
        parts.push({ geo: box(a.gauge + 2.6, 0.14, 4.6, s[0], top + 0.85, s[2],
          tAcross), color: CONCRETE });
        // load/unload ramp at footrest height (rope − drop)
        parts.push({ geo: box(a.gauge + 2.0, 0.26, 4.6, s[0], top - a.cabDrop - 0.16,
          s[2], tAcross), color: [0.5, 0.49, 0.47] });
      } else {
        const H = top - base + 0.8;
        const mast = new THREE.CylinderGeometry(0.3, 0.6, H, 5);
        mast.translate(s[0], base + H / 2, s[2]);
        parts.push({ geo: mast, color: STEEL });
        parts.push({ geo: box(a.gauge + 1.6, 0.4, 0.55, s[0], top + 0.2, s[2],
          tAcross), color: STEEL });
        for (const sgn of [1, -1]) {
          parts.push({ geo: box(0.26, 0.34, 1.5,
            s[0] + px * sgn * a.gauge / 2, top - 0.08,
            s[2] + pz * sgn * a.gauge / 2, tAlong), color: DARK });
        }
      }
    }
  }

  setServerS(arr) {
    const a = Array.isArray(arr) ? arr : [arr];
    for (let i = 0; i < this.lines.length && i < a.length; i++) {
      this.lines[i].targetS = a[i];
    }
  }

  nearest(playerPos) {
    let best = Infinity;
    this.nearestKind = null;
    for (const line of this.lines) {
      for (const car of line.cars) {
        const d = car.position.distanceTo(playerPos);
        if (d < best) { best = d; this.nearestKind = line.kind; }
      }
    }
    return best;
  }

  // world-space delta for a rider standing in a cabin, or null
  update(dt, playerPos) {
    let carry = null;
    this._tick++;
    const lazy = this.farLazy ?? cpuShort();
    for (let li = 0; li < this.lines.length; li++) {
      const line = this.lines[li];
      let d = line.targetS - line.s;
      if (line.system === 'circulating') d -= Math.round(d);   // wrap-shortest
      line.s += d * Math.min(1, dt * 6);
      if (line.system === 'circulating') line.s = ((line.s % 1) + 1) % 1;
      // a far chair line poses its chairs on its stagger frame only (draw
      // only: the positions below are exact every frame)
      const chair = line.kind === 'chair_lift';
      const pose = !chair || !line.posed || this.farEvery <= 1
        || boxDist(line.box, playerPos) <= CHAIR_FAR_M
        || (this._tick + li) % this.farEvery === 0;
      if (!pose && lazy) continue;      // quest: positions = the last pose
      for (let k = 0; k < line.cars.length; k++) {
        const c = cabinPosHinted(line.line, line.s, k, _cab, this.hinted ? line.seg : null);
        const car = line.cars[k];
        _v.set(c.pos[0], c.pos[1], c.pos[2]);
        const prev = line.prev[k];
        car.position.copy(_v);
        if (pose) {
          // the yaw cache (quest-move 2026-11-02; every class since all-res
          // 2026-11-02): a cabin still on the cable segment it was turned on
          // keeps that turn — the same tangent, so lookAt's answer up to
          // rounding (yawCache false: turned every pose, a gate's twin)
          const yk = this.yawCache && this.hinted ? c.side * 16777216 + line.seg[k] : -1;
          if (yk < 0 || !line.yaw || line.yaw[k] !== yk) {
            _look.set(c.pos[0] + c.tangent[0] * c.dir, c.pos[1],
              c.pos[2] + c.tangent[2] * c.dir);
            car.lookAt(_look);
            if (yk >= 0) (line.yaw || (line.yaw = new Int32Array(line.cars.length).fill(-1)))[k] = yk;
          }
          car.updateMatrix();
          // (a chair's matrix goes to its sorted slot in _packChairs)
          if (line.inst) line.inst.setMatrixAt(k, car.matrix);
        }
        if (prev && line.carryHx > 0) {
          _local.copy(playerPos).sub(_v);
          _localQ.copy(car.quaternion).invert();
          _local.applyQuaternion(_localQ);
          if (Math.abs(_local.x) < line.carryHx && Math.abs(_local.z) < line.carryHz
            && _local.y > -1.5 && _local.y < 2.5) {
            carry = { x: _v.x - prev.x, y: _v.y - prev.y, z: _v.z - prev.z,
              floorY: _v.y + 0.18 };
          }
        }
        line.prev[k] = (prev || new THREE.Vector3()).copy(_v);
      }
      if (!pose) continue;
      line.posed = true;
      line.poseTick = this._tick;
      if (line.inst) line.inst.instanceMatrix.needsUpdate = true;
    }
    if (this._chairs) this._packChairs(playerPos);
    return carry;
  }
}

// a line's xz box (both cables) and a point's xz distance to it
function lineBox(a) {
  const b = [Infinity, -Infinity, Infinity, -Infinity];
  for (const side of a.sides) {
    for (const p of side.points) {
      if (p[0] < b[0]) b[0] = p[0];
      if (p[0] > b[1]) b[1] = p[0];
      if (p[2] < b[2]) b[2] = p[2];
      if (p[2] > b[3]) b[3] = p[2];
    }
  }
  return b;
}
// a line's sphere (both cables' points), padded by `pad` m
function lineSphere(a, pad) {
  const b = new THREE.Box3();
  for (const side of a.sides) for (const p of side.points) b.expandByPoint(_v.set(p[0], p[1], p[2]));
  const s = b.getBoundingSphere(new THREE.Sphere());
  s.radius += pad;
  return s;
}
function boxDist(b, p) {
  const dx = Math.max(b[0] - p.x, 0, p.x - b[1]);
  const dz = Math.max(b[2] - p.z, 0, p.z - b[3]);
  return Math.hypot(dx, dz);
}

const _v = new THREE.Vector3();
const _cab = { pos: [0, 0, 0], tangent: [0, 0, 0], cableY: 0, side: 0, sv: 0, dir: 0 };
const _look = new THREE.Vector3();
const _local = new THREE.Vector3();
const _localQ = new THREE.Quaternion();
const _color = new THREE.Color();
const _vp = new THREE.Vector4();
const _cp = new THREE.Vector3();
const drain = (it) => { for (;;) { const r = it.next(); if (r.done) return r.value; } };
